import React from 'react';
import ChartWithLegend from './chart-with-legend';
import Statistic from './statistic';
import { useI18nFormatting } from 'hooks/use-i18n-formatting';
import Neighbourhood, { getNeighbourhoodDemographics } from 'data/neighbourhood';
import styles from './style.module.scss';
import LoadingData from 'components/loading-data';
import { LOCALE_COMMON_PATHS, Namespaces } from 'types/locales';
import { useTranslation } from 'next-i18next';
import { neighbourhoodTestIds } from 'constants/test-constants';

//#region Types
const NeighbourhoodIdKey = 'neighbourhoodId' as const;
const NeighbourhoodDataKey = 'neighbourhood' as const;

type NeighbourhoodWithId = { [NeighbourhoodIdKey]: number };
type NeighbourhoodWithData = { [NeighbourhoodDataKey]: Record<string, unknown> };
type NeighbourhoodDemographicsProps = NeighbourhoodWithId | NeighbourhoodWithData;

const isNeighbourhoodWithId = (props: NeighbourhoodDemographicsProps): props is NeighbourhoodWithId => NeighbourhoodIdKey in props;
const isNeighbourhoodWithData = (props: NeighbourhoodDemographicsProps): props is NeighbourhoodWithData => NeighbourhoodDataKey in props;

//#endregion

const Stats = ({ demographics }: { demographics: Neighbourhood }) => {
  const { t } = useTranslation(Namespaces.COMMON);
  const { formatCurrency, formatNumber } = useI18nFormatting();
  return (
    <div className={styles.component}>
      <div className={styles.stats}>
        <Statistic
          value={formatCurrency(demographics.averageHouseholdIncome) || ''}
          label={t(LOCALE_COMMON_PATHS.ADDRESS_NEIGHBOURHOOD_DEMOGRAPHICS_AVERAGE_HOUSEHOLD_INCOME)}
          testId={neighbourhoodTestIds.averageHouseholdIncome}
        />
        <Statistic
          value={`${formatNumber(demographics.propertiesOwned)} (${demographics.propertiesOwnedPercent}%)`}
          label={t(LOCALE_COMMON_PATHS.ADDRESS_NEIGHBOURHOOD_DEMOGRAPHICS_PROPERTIES_OWNED)}
          testId={neighbourhoodTestIds.propertiesOwned}
        />
        <Statistic
          value={formatNumber(demographics.populationTotal)}
          label={t(LOCALE_COMMON_PATHS.ADDRESS_NEIGHBOURHOOD_DEMOGRAPHICS_TOTAL_POPULATION)}
          testId={neighbourhoodTestIds.populationTotal}
        />
        <Statistic value={`${formatNumber(demographics.propertiesRented)} (${demographics.propertiesRentedPercent}%)`}
          label={t(LOCALE_COMMON_PATHS.ADDRESS_NEIGHBOURHOOD_DEMOGRAPHICS_PROPERTIES_RENTED)}
          testId={neighbourhoodTestIds.propertiesRented}
        />
      </div>
      <ChartWithLegend
        title={t(LOCALE_COMMON_PATHS.ADDRESS_NEIGHBOURHOOD_DEMOGRAPHICS_POPULATION_BY_AGE)}
        data={demographics.ages}
        testId={neighbourhoodTestIds.populationByAge}
      />
    </div>
  );};

const fetchDemographics = async (neighbourhoodId: number) => {
  const data = await getNeighbourhoodDemographics(neighbourhoodId);
  return new Neighbourhood(data);
};

export default function NeighbourhoodDemographics(props: NeighbourhoodDemographicsProps) {
  const demographics = isNeighbourhoodWithData(props) && props.neighbourhood ? new Neighbourhood(props.neighbourhood) : undefined;
  const promise = isNeighbourhoodWithId(props) ? fetchDemographics(props.neighbourhoodId) : undefined;

  if (!demographics && !promise) {
    return null;
  } else if (demographics) {
    return <Stats demographics={demographics} />;
  } else {
    return (
      <LoadingData promise={promise}>
        {demographics => demographics ? <Stats demographics={demographics} /> : null}
      </LoadingData>
    );
  }
}
